/**
 * Daily reminder setting (local time, 100% local preference).
 */
import { Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { BellRing } from "@fleet/icons";
import { reminderTimeOptions } from "@journal/shared";
import { Card, CardContent, Switch } from "@fleet/ui";
import { Select } from "@/components/ui/select";
import { useI18n } from "@/providers/i18n-provider";
import { getNotificationAdapter } from "@/lib/notifications/service";
import { useReminderPreference } from "../hooks/use-daily-reminder";

export function ReminderSettingsCard() {
  const { t } = useI18n();
  const { enabled, time, setEnabled, setTime } = useReminderPreference();

  const handleToggle = (value: boolean) => {
    setEnabled(value);
    if (value) {
      // Ask for permission within the user's gesture (browsers require it).
      void getNotificationAdapter().then(async (adapter) => adapter.requestPermission());
    }
  };

  return (
    <Card data-testid="reminder-card">
      <CardContent class="flex flex-col gap-3 p-4">
        <div class="flex items-center justify-between gap-3">
          <div class="flex items-center gap-2.5">
            <span class="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <BellRing class="h-4 w-4" />
            </span>
            <div>
              <p class="text-sm font-semibold text-foreground">{t(msg`Daily writing reminder`)}</p>
              <p class="text-xs text-muted-foreground/70">
                {t(msg`A gentle local nudge — nothing leaves your device.`)}
              </p>
            </div>
          </div>
          <Switch checked={enabled()} onCheckedChange={handleToggle} />
        </div>

        <Show when={enabled()}>
          <label class="flex items-center gap-2 text-sm text-muted-foreground">
            {t(msg`Remind me at`)}
            <Select
              value={time()}
              onChange={setTime}
              options={reminderTimeOptions()}
              aria-label={t(msg`Remind me at`)}
              data-testid="reminder-time"
            />
          </label>
        </Show>
      </CardContent>
    </Card>
  );
}
